'use strict';

const _            = require('lodash');
const Promise      = require('bluebird');
const EventEmitter = require('wolfy87-eventemitter');
const Logger       = require('./Logger');
const Events       = require('./Events');
const Connection   = require('./Connection');

/* @private Private variables */
let _Connection = Connection;  // Defaults to global module
let _Lobby = null;  // No need to require Lobby from start
let _LocalChannel = null;

let LocalChannelEvents = ['BACK_TO_INBOX'];
/**
 * Represents a Chat instance. It holds and manages all the activity that can occur in any
 * conversation, as well as all Events related to chat activity.
 */
class Chat extends EventEmitter {
    /**
     * Chat class constructor
     * @param {Object} options Chat data and/or options
     * @param {Object} config  Chat configuration
     */
    constructor (options, config = {}) {
        super();
        this.setConfig(config);
        this.setData(options);
        this.readAnyUnreadMessageFromHistory();
        this.assignAttempts = 0;
        this.intervals = {};
        this.listeningEvents = {};
        this.initEvents();
    }

    /**
     * Allows to inject external dependencies
     * @param  {Object} params object with all dependencies to inject
     * @static
     */
    static configure (params) {
        _Connection = params.Connection;
        _Lobby      = params.Lobby;
        _LocalChannel = params.LocalChannel;
    }

    /**
     * Initializes the chat data (loads it from the server or creates a new chat)
     * and creates a new Chat object instance, which returns via promise resolve argument
     *
     * @param  {Object}   options       Options array which can also contain chat data.
     * If options.id exists, then Chat will be recovered. Otherwise, a new Chat will be created.
     * @param  {Object}   config        Configuration array. If empty takes the default one
     * @param  {Bool}     historyWatch  If the chats:join performed by 'watch' agents have to be included in history
     * @return {Promise<Chat>}    A promise fulfilled with a Chat instance, which will be
     * brand new if Chat didn't exist before, or just a Chat recovered from server otherwise.
     * @static
     */
    static init (options = {}, config = {}, historyWatch = false) {
        // if the chat ID is provided in options, download the chat information
        // from an already created chat
        if (options.id) {
            return this._loadChat(options, historyWatch)
                .then((res) => Promise.resolve(new Chat(res.chat, config)));
        }
        // if there isn't, then a new chat has to be created
        return this._create(options)
            .then((res) => Promise.resolve(new Chat(res.chat, config)))
            .then((chat) => {
                // Now that chat is instance of Chat, add it to Lobby (if any)
                if (_Lobby) {
                    _Lobby.addChat(chat);
                }
                return chat;
            });
    }

    /**
     * Look for an agent to attend the chat using the provided algorithm.
     *
     * @param {String} algorithm    Algorithm name
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    searchAgent (algorithm) {
        if (this.closed) {
            return this._handleChatClosedError();
        }
        const chatData = {
            chatId: this.getId(),
            algorithm: algorithm || false,
            maxAttempts: this.config.assignMaxAttempts,
            interval: this.config.assignTimeout
        };
        // send assignation event
        return Events.send('chats:assign', chatData);
    }

    /**
     * Update the current chat data
     *
     * @param {Object}  chat       Chat
     * @param {Object}  user       User
     * @param {Boolean} watcher    If the given user is a watcher or a normal user
     */
    updateData (chat, user={}, watcher=false) {
        this.room = chat.room;
        this.closed = chat.closed;
        this.lastActivity = chat.lastActivity;
        this.comments = chat.comments;

        if (!_.isEmpty(user)) {
            user = this._getEssentialUserInfo(user);
            const users = watcher ? this.watchers : this.users;
            const userIds = this._getUserIds(users);
            if (userIds.indexOf(user.id) === -1) {
                users.push(user);
            }
        }
    }

    /**
     * Invite another user to chat
     * @param {Object} options Options object with all necessary data (user, transfer, comment)
     */
    invite (options) {
        if (this.closed) {
            return this._handleChatClosedError();
        }
        const user = options.user;
        // TODO: validate user information
        const info = {
            userId: user.id,
            chatId: this.getId(),
            senderId: _Connection.me().id,
            transfer: options.transfer,
            comment: options.comment || null
        };

        return Events.send('invitations:new', info);
    }

    /**
     * Joins the current user to this chat
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    join (mode = false) {
        if (this.closed) {
            return this._handleChatClosedError();
        }
        let data = {
            userId: _Connection.me().id,
            chatId: this.getId()
        };
        if (mode) {
            data.mode = mode;
        }
        return Events.send('chats:join', data)
            .then((res) => {
                this.updateData(res.chat, res.user, mode === 'watch');
                return res;
            });
    }

    /**
     * Removes the current user from this chat
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    leave (mode = false) {
        let data = {
            userId: _Connection.me().id,
            chatId: this.getId()
        };
        if (mode) {
            data.mode = mode;
        }
        return Events.send('chats:leave', data);
    }

    /**
     * The current user intervenes this chat
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    intervene () {
        return Events.send('chats:intervene', {
            interventorId: _Connection.me().id,
            chatId: this.getId()
        })
        .then((res) => {
            // intervened users were in the chat as users and are now watchers
            const intervenedUserIds = this._getIntervenedUserIds(res.chat.watchers);
            this._swapUsersAndWatcher(intervenedUserIds, _Connection.me().id);
            this.updateData(res.chat);
            return res;
        });
    }

    /**
     * Send user activity
     * @param  {String} type    User activity type
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    sendUserActivity (type) {
        if (this.closed) {
            return this._handleChatClosedError();
        }
        return Events.send('users:activity', {
            type: type,
            userId: _Connection.me().id,
            chatId: this.getId()
        });
    }

    /**
     * Sends a message
     * @param  {String} message     Message to send
     * @param {Function} onCreate   Function to call on message creation
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    sendMessage (message, onCreate) {
        if (this.closed) {
            return this._handleChatClosedError();
        }

        const sender = _Connection.me();
        const messageInfo = {
            message: message,
            chatId: this.getId(),
            sender: sender.id
        };
        const msgEvent = Events._create('messages:new', messageInfo);
        // execute a function (if present) on message creation
        // this is, basically, to allow the interface to print the new message with its id
        onCreate && onCreate(msgEvent.data.eventId, message, sender);
        return msgEvent.send();
    }

    /**
     * Send a message read event
     * @param {Object} msg       Message information object
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    readMessage (messageId) {
        return Events.send('messages:read', {
            messageId: messageId,
            userId: _Connection.me().id
        });
    }

    /**
     * Sends media
     * @param  {Object} media       Media object description
     * @param {Function} onProgress Callback to run while file is being uploaded, @optional
     * @return {Promise<Object>}    A Promise fulfilled with the server response
     */
    sendMedia (media, onProgress) {
        if (this.closed) {
            return this._handleChatClosedError();
        }
        // create an object with the relevant info
        const info = {
            senderId: _Connection.me().id,
            chatId: this.getId(),
            token: _Connection.getToken(),
            name: media.name,
            size: media.size,
            type: media.type,
            isInline: (media.isInline !== undefined) ? media.isInline : false
        };
        // upload media to the server
        return _Connection.upload({ media, info }, onProgress);
    }

    /**
     * Sends the chat back to the origin room inbox
     * @param  {Object} options     Options object containing chat information
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    toInbox (options = {}) {
        if (this.closed) {
            return this._handleChatClosedError();
        }
        const info = {
            userId    : _Connection.me().id,
            chatId    : this.getId(),
            roomId    : options.room || null,
            comment   : options.comment || null
        };
        _LocalChannel.backToInbox(info);
        return Events.send('chats:toinbox', info);
    }

    /**
     * Closes the chat, if not already closed
     * @return {Promise<Boolean|Error>} A Promise fulfilled with the result of the operation
     */
    close (extraData = null) {
        if (this.closed) {
            return this._handleChatClosedError();
        }
        return Events.send('chats:close', {
            chatId: this.getId(),
            userId: _Connection.me().id,
            extraData: extraData
        })
            .then((res) => {
                this.closed = res.success;
                // If there's a Lobby set up, remove this chat from it
                if (res.success && _Lobby) {
                    _Lobby.removeChat(this.id);
                }
                return res.success;
            });
    }

    /**
     * Checks if the chat has other users
     * @return {Boolean} True if it has other users, false otherwise
     */
    hasOtherUsers () {
        return this.users.length > 0;
    }

    /**
     * Sets the current chat data
     * @param {Object} data Chat data
     */
    setData (data) {
        this.id       = data.id       || null;
        this.room     = data.room     || null;
        this.creator  = data.creator  || null;
        this.source   = data.source   || null;
        this.lang     = data.lang     || null;
        this.closed   = data.closed   || false;
        this.history  = data.history  || [];
        this.comments = data.comments || [];
        this.users    = data.users    ? this._getEssentialUserInfoFromUsers(data.users)    : [];
        this.watchers = data.watchers ? this._getEssentialUserInfoFromUsers(data.watchers) : [];
        this.lastActivity = data.lastActivity || null;
        if (data.queueData) {
            this.queueData = data.queueData;
        }
        if (data.inQueue) {
            this.inQueue = data.inQueue;
        }
    }

    /**
     * Retrieves the chat ID
     * @return {String} Chat ID
     */
    getId () {
        return this.id;
    }

    /**
     * Import external history into the current chat
     * @param {Object} history history to be imported
     * @return {Promise<Object|Error>} A Promise fulfilled with the resulting
     * history after merging the imported and the previous one
     */
    importHistory (history) {
        if (this.closed) {
            return this._handleChatClosedError();
        }
        return Events.send('history:import', {
            chatId: this.getId(),
            history: history
        })
            .then((res) => {
                return res.history;
            });
    }

    /**
     * Executed always when a user joins a chat
     * @param  {Object} data Contains:
     *                           {Object} chat: The chat information object
     *                           {Object} user: The user who has joined the chat
     */
    onUserJoin (data) {
        // update chat property to include new users
        const isWatcher = data.chat.watchers && (data.chat.watchers.indexOf(data.user.id) !== -1);
        this.updateData(data.chat, data.user, isWatcher);
        Logger.log('User JOIN: '+data.user.name);
    }

    /**
     * Executed always when a user performs an activity (typically writing or stop writing)
     * @param  {Object} data Contains:
     *                           {String} type: Activity type
     *                           {Object} user: User information
     */
    onUserActivity () {}

    /**
     * Executed always when a new message is received
     * @param  {Object} data Contains:
     *                           {Object} message: Message information object
     */
    onMessageReceived (data) {
        if (!this._itsMe(data.message.sender)) {
            // emit read
            this.readMessage(data.message.id);
        }
    }

    /**
     * Executed always when a message is read by another user
     * @param  {Object} data Contains:
     *                           {Object} message: Message information object
     */
    onMessageRead (data) {
        const message = data.message;
        // only double-check the message when it is read by everybody in the chat
        // we subtract one because the sender of the message has already read it
        const messageReadByEveryone = (this.users.length-1 === message.readBy.length);

        if (this._itsMe(message.sender) && messageReadByEveryone) {
            // double-check message
            Logger.log('DOUBLE-CHECK: ' + message.id);
            this.trigger('message:doublecheck', [{ id: message.id }]);
        }
    }

    /**
     * Executed always when a user leaves the chat
     * @param  {Object} data Contains:
     *                           {String} userId: User ID
     */
    onUserLeave (data) {
        const userIndex = this._getUserIds(this.users).indexOf(data.userId);
        if (userIndex !== -1) {
            this.users.splice(userIndex, 1);
        }
    }

    /**
     * Executed always when the chat is closed
     * @param  {Object} data Contains:
     *                           {String} chatId: Chat ID
     */
    onChatClosed (data) {
        this.closed = (data.chatId === this.id);
    }

    /**
     * Executed always when the chat is intervened
     * @param  {Object} data                Contains:
     *                                          {Object} chat         Chat
     *                                          {Object} interventor  User
     * @param  {Array} intervenedUserIds    IDs of the intervened users
     */
    onChatIntervened (data, intervenedUserIds) {
        this._swapUsersAndWatcher(intervenedUserIds);
        this.users.push(this._getEssentialUserInfo(data.interventor));
        this.updateData(data.chat);
        if (_Lobby) {
            // Update Lobby chats without unsubscribing
            // from chat events
            const chatId = data.chat.id;
            _Lobby.addChat(_Lobby.chats[chatId], 'watch');
            delete _Lobby.chats[chatId];
        }
    }

    /**
     * Executed always when chat could not be assigned
     */
    onForeverAlone () {}

    /**
     * Executed always when a user in the chat has been updated
     */
    onUserUpdated (data) {
        // Update the user information
        const userIndex = this.users.map((item) => { return item.id; }).indexOf(data.user.id);
        this.users[userIndex] = this._getEssentialUserInfo(data.user);
    }

    /**
     * Executed always when the position of the chat in a queue has been updated
     * @param {Object} data
     */
    onQueueUpdate (data) {
        this.queueData = data;
        // If the position is 0, the chat just popped
        if (data.queuePosition == 0) {
            this.inQueue = false;
        }
    }

    /**
     * Binds all chat server events to this class' methods
     * @param  {array}      missing If missing events are specified, these are the only ones to init, else init them all
     */
    initEvents (missing = null) {
        if (!missing || (missing.indexOf('user:join') !== -1)) {
            /** Fired whenever a user joins a chat */
            this.subscribe('user:join', (data) => {
                if (data.chat.id === this.id) {
                    // only call pre function if it exists
                    this.preUserJoin && this.preUserJoin(data);
                    this.onUserJoin(data);

                    this.trigger('user:joined', [data]);
                }
            });
        }

        if (!missing || (missing.indexOf('user:leave') !== -1)) {
            /** Fired whenever a user leaves the chat */
            this.subscribe('user:leave', (data) => {
                if (data.chatId === this.id && !this._itsMe(data.userId)) {
                    this.preUserLeave && this.preUserLeave(data);
                    data.user = this.users.find((user) => {
                        return user.id === data.userId;
                    });
                    this.onUserLeave(data);

                    this.trigger('user:left', [data]);
                }
            });
        }

        if (!missing || (missing.indexOf('user:activity') !== -1)) {
            /** Fired whenever a user activity is received */
            this.subscribe('user:activity', (data) => {
                if (data.chatId === this.id && !this._itsMe(data.userId)) {
                    this.preUserActivity && this.preUserActivity(data);
                    this.onUserActivity(data);

                    this.trigger('user:activity', [data]);
                }
            });
        }

        if (!missing || (missing.indexOf('message:new') !== -1)) {
            /** Fired whenever a new message is received */
            this.subscribe('message:new', (data) => {
                if (data.message.chat === this.id) {
                    this.preMessageReceived && this.preMessageReceived(data);
                    this.onMessageReceived(data);

                    this.trigger('message:received', [data]);
                }
            });
        }

        if (!missing || (missing.indexOf('message:read') !== -1)) {
            /** Fired whenever a message is read by the other user/s */
            this.subscribe('message:read', (data) => {
                if (data.message.chat === this.id) {
                    this.preMessageRead && this.preMessageRead(data);
                    this.onMessageRead(data);

                    this.trigger('message:read', [data]);
                }
            });
        }

        if (!missing || (missing.indexOf('chat:closed') !== -1)) {
            /** Fired whenever the chat is closed */
            this.subscribe('chat:closed', (data) => {
                if (data.chatId === this.id) {
                    this.preChatClosed && this.preChatClosed(data);
                    this.onChatClosed(data);

                    this.trigger('chat:closed', [data]);
                }
            });
        }

        if (!missing || (missing.indexOf('chat:intervened') !== -1)) {
            /** Fired whenever the chat is intervened */
            this.subscribe('chat:intervened', (data) => {
                if (data.chat.id === this.id) {
                    this.preChatIntervened && this.preChatIntervened(data);
                    const intervenedUserIds = this._getIntervenedUserIds(data.chat.watchers);
                    this.onChatIntervened(data, intervenedUserIds);

                    data.intervenedUsers = this.watchers.filter(w => (intervenedUserIds.indexOf(w.id) !== -1));
                    this.trigger('chat:intervened', [data]);
                }
            });
        }

        if (!missing || (missing.indexOf('forever:alone') !== -1)) {
            /** Fired whenever the chat wasn't accepted by any agent */
            this.subscribe('forever:alone', (data) => {
                if (data.chatId === this.id) {
                    this.preForeverAlone && this.preForeverAlone();
                    this.onForeverAlone();

                    this.trigger('forever:alone', [data]);
                }
            });
        }

        if (!missing || (missing.indexOf('user:updated') !== -1)) {
            /** Fired whenever the chat wasn't accepted by any agent */
            this.subscribe('user:updated', (data) => {
                if (data.chatId === this.id) {
                    this.preUserUpdated && this.preUserUpdated();
                    this.onUserUpdated(data);

                    this.trigger('user:updated', [data]);
                }
            });
        }

        if (!missing || (missing.indexOf('queue:update') !== -1)) {
            /** Fired whenever an update is received about the chat waiting in a queue */
            this.subscribe('queue:update', (data) => {
                this.preQueueUpdate && this.preQueueUpdate();
                this.onQueueUpdate(data);

                this.trigger('queue:update', [data]);
            });
        }

        if (!missing || (missing.indexOf('chat:timer:expiryAlert') !== -1)) {
            /** Fired whenever the chat is close to expire according to the configured settings */
            this.subscribe('chat:timer:expiryAlert', (data) => {
                if (data.chatId === this.id) {
                    this.preChatTImerExpiryAlert && this.preChatTImerExpiryAlert();

                    this.trigger('chat:timer:expiryAlert', [data]);
                }
            });
        }

        if (!missing || (missing.indexOf('chat:toinbox') !== -1)) {
            /** Fired whenever the chat is sent back to inbox */
            this.subscribe('chat:toinbox', (data) => {
                if (data.chatId === this.id) {
                    this.trigger('chat:toinbox', [data]);
                }
            });
        }

        if (!missing || (missing.indexOf('BACK_TO_INBOX') !== -1)) {
            /** Fired whenever another tab performed a back to inbox */
            this.subscribeForLocalChannel('BACK_TO_INBOX', (data) => {
                if (data.chatId === this.id) {
                    this.trigger('BACK_TO_INBOX', [data]);
                }
            });
        }
    }

    checkListeningEvents () {
        const mustHave = [
            'user:join',
            'user:leave',
            'user:activity',
            'message:new',
            'message:read',
            'chat:closed',
            'chat:intervened',
            'forever:alone',
            'user:updated',
            'queue:update',
            'chat:toinbox',
            'BACK_TO_INBOX'
        ];
        let missing = [];
        mustHave.forEach((event) => {
            if (Object.keys(this.listeningEvents).indexOf(event) === -1) {
                missing.push(event);
            }
        });
        if (missing.length > 0) {
            this.initEvents(missing);
        }
    }

    /**
     * Subscribes this chat to an event type
     * @param {String} type Event name
     * @param {Function} callback callback to fire when event takes place
     */
    subscribe (type, callback) {
        if (type in this.listeningEvents) {
            throw new Error('Already listening to '+type);
        }
        this.listeningEvents[type] = callback;
        return Events.subscribe(type, callback);
    }

    subscribeForLocalChannel (type, callback) {
        if (type in this.listeningEvents) {
            throw new Error('Already listening to ' + type);
        }
        this.listeningEvents[type] = callback;
        return _LocalChannel.on(type, callback);
    }

    /**
     * Remove all listeners related to type (if specified) or all listeners otherwise
     */
    unsubscribe (type) {
        if (type) {
            Events.unsubscribe(type, this.listeningEvents[type]);  // remove all listeners in this chat related to the type
            this.removeAllListeners(type);
            delete this.listeningEvents[type];
        } else {

            // remove all listeners related to this chat
            for (let event in this.listeningEvents) {
                // Avoid LocalChannel events when unsubscribing from connection
                if (LocalChannelEvents.indexOf(event) === -1) {
                    Events.unsubscribe(event, this.listeningEvents[event]);
                }
            }
            this.removeAllListeners();
            this.listeningEvents = {};
        }
    }

    /**
     * Modifies the configuration object
     */
    setConfig (config) {
        this.config = Object.assign({}, config);
    }

    /**
     * Returns if a userId is equal to this user's id
     * @param  {String}  userId  User ID
     * @private
     */
    _itsMe (userId) {
        return (_Connection.me().id === userId);
    }

    /**
     * Returns if a user is in the chat (i.e. in users or watchers)
     * @return {Boolean}
     * @private
     */
    _userInChat () {
        return ([...this.users, ...this.watchers].map((user) => user.id).indexOf(_Connection.me().id) !== -1);
    }

    /**
     * Marks all messages as read after loading the chat from the server
     * @param  {Object} history Chat history
     * @return {Array} An array with all messages that have been read after processing
     * the history
     */
    readAnyUnreadMessageFromHistory () {
        if (!this._userInChat()) {
            // if the user is not in the chat, do not send messages:read events
            return Promise.resolve();
        }

        const messages = {};
        const haveIt = (messageId) => {
            return (Object.keys(messages).indexOf(messageId) !== -1);
        };
        const importedMessagesIds = this.history.reduce((toReturn, entry) => {
            if (_.startsWith(entry.event, 'messages') && entry.imported) {
                toReturn.push(entry.data.message.id);
            }
            return toReturn;
        }, []);
        // store all messages in an array filling the readBy array
        // with all users that have read each message
        this.history && this.history.map((entry) => {
            const { event, trigger } = entry;
            const { message } = entry.data;
            if (_.startsWith(event, 'messages') && !this._itsMe(message.sender) && (importedMessagesIds.indexOf(message.id) === -1)) {
                if (_.endsWith(event, 'new') && !haveIt(message.id)) {
                    messages[message.id] = message;
                } else if (_.endsWith(event, 'read')) {
                    if (!haveIt(message.id)) {
                        messages[message.id] = message;
                    }
                    const readBy = messages[message.id].readBy;
                    if (readBy.indexOf(trigger) === -1) {
                        readBy.push(trigger);
                    }
                }
            }
        });
        // loop through the messages object and read the messages
        // the current user hasn't read
        const messagesToRead = {};
        Object.keys(messages).forEach((messageId) => {
            const message = messages[messageId];
            if (message.readBy.indexOf(_Connection.me().id) === -1) {
                messagesToRead[messageId] = message;
            }
        });
        Promise.map(Object.keys(messagesToRead), (msgId) => this.readMessage(msgId));
        return messagesToRead;
    }

    /**
     * Return the users that have been intervened
     * @param {Array}  newWatcherIds    Ids of the new users that are in watch mode
     */
    _getIntervenedUserIds (newWatcherIds) {
        const userIds = this._getUserIds(this.users);
        return _.intersection(userIds, newWatcherIds);
    }

    /**
     * Return the ids of the given users
     * @param {Array}  users    Users
     */
    _getUserIds (users) {
        return users.map(u => u.id);
    }

    /**
     * Retrieve a user object that only contains their ID and Name
     * @param {Object}  user    User
     */
    _getEssentialUserInfo (user) {
        const userData = {
            id: user.id,
            name: user.name,
            nickname: String(user.nickname),
            isAgent: (user.providerId !== ''),
            lastActivity: user.lastActivity,
            gender: user.gender
        };
        if (!userData.isAgent) {
            // If user is not an agent, add a little more info
            Object.assign(userData, {
                contact: user.contact,
                extraInfo: user.extraInfo
            });
        }
        return userData;
    }

    /**
     * Retrieve an array of users that only contains their IDs and Names
     * @param {Array}  users    Users
     */
    _getEssentialUserInfoFromUsers (users) {
        return users.map(user => this._getEssentialUserInfo(user));
    }

    /**
     * Swaps users and a watcher. Users will become watchers and viceversa
     * @param {Array}  userIds      User IDs
     * @param {String} watcherId    User ID
     */
    _swapUsersAndWatcher (userIds=[], watcherId='') {
        if (watcherId) {
            const watcherIndex = this._getUserIds(this.watchers).indexOf(watcherId);
            const user = this.watchers.splice(watcherIndex, 1)[0];
            this.users.push(user);
        }

        if (userIds.length > 0) {
            for (var i=0; i<this.users.length; i++) {
                if (userIds.indexOf(this.users[i].id) !== -1) {
                    const watcher = this.users.splice(i, 1)[0];
                    this.watchers.push(watcher);
                }
            }
        }
    }

    /**
     * Loads a chat information using its ID, returning its information in a Promise
     * @param  {Object}     options       contains id (chat id) and fields (extra fields to get)
     * @param  {Bool}       historyWatch  if the chats:join performed by 'watch' agents have to be included in history
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     * @private
     */
    static _loadChat (options, historyWatch=false) {
        let fields = options.fields;
        // define a function to avoid code replication
        const addField = (field) => {
            if (!fields) {
                fields = field;
            } else if (fields.indexOf(field) === -1) {
                fields = fields.split(',');
                fields.push(field);
                fields = fields.join(',');
            }
        };

        // if fields don't contain users, add it
        addField('users');
        // if fields don't contain watchers, add it
        addField('watchers');
        // if fields don't contain history, add it
        addField('history');
        // get the chat information using its ID
        return Events.send('chats:get', {
            chatId: options.id,
            fields: fields
        })
        .then((res) => {
            if (!historyWatch) {
                res.chat.history = this._getHistoryWithoutWatch(res.chat.history);
            }
            return res;
        });
    }

    /**
     * Returns the given chat history without any actions performed by watchers
     * @param  {Array}     history     chat history
     * @return {Array}
     * @private
     */
    static _getHistoryWithoutWatch (history) {
        _.remove(history, (entry) => {
            const isWatcherJoin  = entry.event === 'chats:join'  && entry.data.mode === 'watch';
            const isWatcherLeave = entry.event === 'chats:leave' && entry.data.mode === 'watch';

            return isWatcherJoin || isWatcherLeave;
        });
        return history;
    }

    /**
     * Creates a new chat in the server, returning the chat's information in a Promise
     * @param  {Object}     options  Chat creation options
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     * @private
     */
    static _create (options) {
        const user = _Connection.me();
        if (!user) {
            return Promise.reject({
                message: 'No connection open',
                type: 'connection'
            });
        }
        // create the chat in the server
        let data = {
            room: options.room,
            source: options.source,
            lang: options.lang,
            creator: user.id
        };
        if (options.history) data.history = options.history;
        return Events.send('chats:create', data);
    }

    /**
     * Handle any error regarding this class that needs to reject
     * @param {String} errorMessage Error message to be sent back
     */
    _handleChatClosedError (errorMessage = 'Chat is closed') {
        return Promise.reject({
            message: errorMessage,
            type: 'chat-closed'
        });
    }

}

module.exports = Chat;



// WEBPACK FOOTER //
// ./src/Chat.js